<template>
  <div>
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="border-0 shadow-sm rounded-4">
          <template v-slot:header>
            <div class="d-flex align-items-center justify-content-between p-3">
              <div class="d-flex align-items-center">
                <div class="icon-circle bg-primary text-white me-3">
                  <vue-feather type="award" size="20"></vue-feather>
                </div>
                <div>
                  <h4 class="mb-0 fw-bold text-primary">Subscription Plans</h4>
                  <p class="text-muted mb-0 small">Upgrade your plan to access more features and manage more employees</p>
                </div>
              </div>
            </div>
          </template>

          <!-- Subscription Info Alert -->
          <div class="p-3">
            <div class="alert alert-light border rounded-4 shadow-sm">
              <div class="d-flex align-items-center">
                <div class="icon-circle-sm bg-info text-white me-3">
                  <vue-feather type="info" size="16"></vue-feather>
                </div>
                <div>
                  <h5 class="mb-1 fw-bold">Special Offer!</h5>
                  <p class="mb-0">Get one month free by renewing your subscription for 12 months.</p>
                  <p v-if="subscription.package.name === 'Trial'" class="mb-0 mt-2 text-danger">
                    You are currently on the Trial Version from {{ formatDate(subscription.from) }}. Due {{ formatDate(subscription.to) }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-5">
            <Spinner spinner-class="mb-1" />
            <p class="text-muted">Loading subscription plans...</p>
          </div>

          <!-- Subscription Plans -->
          <div v-else class="px-3 pb-4">
            <div class="row g-4">
              <div class="col-xl-3 col-lg-6 col-md-6" v-for="pack in packages" :key="pack.id">
                <div class="pricing-card card h-100 border-0 shadow-sm rounded-4"
                     :class="{'border-primary border-2': subscription.package.name === pack.name}">
                  <div class="card-header py-3 text-center"
                       :class="pack.name === 'Enterprise' ? 'bg-primary text-white' : 'bg-light'">
                    <div class="d-flex justify-content-center mb-2">
                      <div class="icon-circle-sm" :class="pack.name === 'Enterprise' ? 'bg-white text-primary' : 'bg-primary text-white'">
                        <vue-feather :type="getPackageIcon(pack.name)" size="16"></vue-feather>
                      </div>
                    </div>
                    <h4 class="fw-bold mb-0">{{ pack.name }}</h4>
                  </div>
                  <div class="card-body text-center p-4">
                    <div v-if="pack.name !== 'Free'" class="mb-3">
                      <h2 class="fw-bold mb-0">{{ formatNumber(pack.price) }}<small class="text-muted">/mo</small></h2>
                      <p class="text-muted">Ksh. {{ formatNumber(pack.price * 11) }}/yr <span class="badge bg-danger">save 9%</span></p>
                    </div>
                    <div v-else class="mb-3">
                      <h2 class="fw-bold mb-0">Free</h2>
                      <p class="text-muted">Basic plan</p>
                    </div>

                    <div class="employee-count mb-3 p-2 rounded-4 bg-light">
                      <h5 class="fw-bold mb-0">
                        <vue-feather type="users" size="16" class="me-1"></vue-feather>
                        {{ pack.employees === 1000 ? 'Unlimited' : pack.employees }} employees
                      </h5>
                    </div>

                    <ul class="list-unstyled mt-3 mb-2 text-start">
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Payroll management
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Employee management
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Leave management
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Employee portal
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Secure payslips
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Automated payslip emailing
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Loan Deduction
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        All By-products (Including Banks)
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Multiple payroll runs
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Annual statistics
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Employee statistics
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        Audit trail
                      </li>
                      <li class="mb-2">
                        <vue-feather type="check-circle" size="16" class="text-success me-2"></vue-feather>
                        24/7 support
                      </li>
                    </ul>
                  </div>
                  <div class="card-footer bg-transparent border-0 p-4">
                    <button v-if="pack.name !== 'Free'"
                            type="button"
                            class="btn btn-lg btn-primary rounded-pill w-100"
                            :disabled="subscription.package.name === pack.name || pack.employees <= employeesNo"
                            @click="selectPackage(pack); $refs.stkModal.stkModalOpen()">
                      <vue-feather type="arrow-up-circle" size="16" class="me-1"></vue-feather>
                      {{ subscription.package.name === pack.name ? 'Current Plan' : 'Upgrade' }}
                    </button>
                    <button v-else
                            type="button"
                            class="btn btn-lg btn-primary rounded-pill w-100"
                            :disabled="subscription.package.name === pack.name || pack.employees <= employeesNo"
                            @click="freeSubscription">
                      <vue-feather type="check-circle" size="16" class="me-1"></vue-feather>
                      {{ subscription.package.name === pack.name ? 'Current Plan' : 'Select Free Plan' }}
                    </button>

                    <div v-if="subscription.package.name === pack.name" class="mt-3 text-center">
                      <Badge pill type="success" class="badge-lg">
                        <vue-feather type="check-circle" size="14" class="me-1"></vue-feather>
                        Current plan
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <StkModal
        ref="stkModal"
        type="upgrade"
        :package_id="package_id"
        :package_name="package_name"
        :price="price"
    ></StkModal>

    <!-- IntaSend Payment Button -->
<!--    <button-->
<!--        class="intaSendPayButton d-none"-->
<!--        ref="upgradeBtn"-->
<!--        data-country="KE"-->
<!--        :data-amount="amount"-->
<!--        data-currency="KES"-->
<!--        :data-email="organization.email"-->
<!--    ></button>-->
  </div>
</template>

<script>
import subscriptionService from "@/api/subscriptionService";
import {decrypt} from "@/utils/crypto";
import formatDate from "../../utils/formatDate";
import formatNumber from "../../utils/formatNumber";
import StkModal from "@/components/modals/Stk-Modal.vue";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';

export default {
  name: "Upgrade",
  components: {
    StkModal,
    VueFeather
  },
  data() {
    return {
      packages: [
        {
          name: '',
          price: 0,
          employees: 0
        }
      ],
      subscription: {
        package: {}
      },
      phone: '',
      duration: 1,
      package_id: '',
      package_name: '',
      price: 0,
      amount: 0,
      upgradeModal: false,
      redirectUrl: process.env.VUE_APP_BASE_URL + 'paymentSuccess',
      organization: {},
      loading: true,
      employeesNo: 0
    }
  },

  methods: {
    formatNumber,
    formatDate,
    getPackageIcon(name) {
      switch(name) {
        case 'Free':
          return 'gift';
        case 'Basic':
          return 'package';
        case 'Standard':
          return 'star';
        case 'Enterprise':
          return 'award';
        default:
          return 'box';
      }
    },
    disableUpgradeBtn(plan) {
      if (this.subscription.package.name === plan.name) {
        return 'd-none'
      } else if (plan.employees <= this.employeesNo) {
        return 'disabled'
      } else {
        return ''
      }
    },
    selectPackage: function (item) {
      this.package_id = item.id
      this.package_name = item.name
      this.price = item.price
    },
    freeSubscription: async function (e) {
      loadingButton(e)
      await subscriptionService.freeSubscription()
      loadingButton(e, false)
    },
  },

  mounted: async function () {
    new window.IntaSend({
      publicAPIKey: process.env.VUE_APP_INTASEND_API_KEY,
      live: process.env.NODE_ENV !== 'development',
      redirectURL: this.redirectUrl
    })
  },

  async created() {
    let packages = await subscriptionService.getPackages()
    this.loading = false
    this.packages = packages.filter(function(item){ return item['name'] !== 'Trial' })
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
    this.employeesNo = JSON.parse(decrypt(sessionStorage.getItem('employeesNo')))
    this.phone = this.organization.phone
  },

}
</script>

<style scoped>
/* Icon styles */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-circle-sm {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Card styles */
.pricing-card {
  transition: all 0.3s ease;
}

.pricing-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

.employee-count {
  transition: all 0.3s ease;
}

/* Badge styles */
.badge-lg {
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
}

/* Button hover effects */
.btn-primary.rounded-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pricing-card {
    margin-bottom: 1rem;
  }
}
</style>
